import { useCallback, useEffect, useMemo, useState } from "react";
import { listMembers } from "../services/memberService";

/**
 * useTenantMembers — carrega a equipe do tenant (`GET /members`) para exibir e
 * escolher responsáveis por lead.
 *
 * Cada item da resposta é um `TenantMember` e carrega **três ids distintos** —
 * confundi-los é a principal fonte de bug nesta área:
 *   - `m.id`     → id do `TenantMember`. É o que o **MessageCenter** grava em
 *                  `ChatSession.assignedToId` (campo com FK).
 *   - `m.userId` → id do `User`. É o que o **Lead** grava em `Lead.assignedTo`
 *                  (campo solto, sem FK/relation — o payload do lead devolve só
 *                  o uuid, nunca um objeto com nome).
 *   - `m.user`   → `{ id, name, email, avatarUrl }` (dados de exibição).
 *
 * Por isso o mapa `byUserId` é chaveado por `m.userId`: é ele que casa com
 * `lead.assignedTo`.
 *
 * Degrada com elegância: se a request falhar, `error` vira `true` e `members`
 * fica vazio — quem consome deve continuar renderizando o resto da tela.
 *
 * @returns {{ members: Array, byUserId: Record<string, Object>, loading: boolean, error: boolean, reload: () => void }}
 */
export function useTenantMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);

    listMembers()
      .then((res) => {
        if (!active) return;
        setMembers(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!active) return;
        setMembers([]);
        setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  const byUserId = useMemo(
    () =>
      members.reduce((acc, m) => {
        if (m?.userId) acc[m.userId] = m;
        return acc;
      }, {}),
    [members],
  );

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  return { members, byUserId, loading, error, reload };
}
